import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import StatsBar from './components/StatsBar';
import Footer from './components/Footer';
import StockProductRow from '../shared/StockProductRow';
import StockMappingModal from '../shared/StockMappingModal';
import { TableContext } from '../../../contexts/TableContext';
import Table from '../../../components/TableBox/Table';
import { buildHeadCells } from '../../../utils/methods';
import { VALIDATION_FIELD_MAPPING } from '../../../utils/appConstant';
import { Modules, AuthRoutes, SortOrder } from '../../../enums';
import useFetch from '../../../hooks/useFetch';
import MonthlyStockService from '../../../services/monthlyStock.service';

// ─── Column view type ─────────────────────────────────────────────────────────

type ColumnView = 'all' | 'values' | 'qty';

// ─── Head cells (all columns — filtered inside component per view) ────────────

const ALL_HEAD_CELLS = buildHeadCells(
  [
    VALIDATION_FIELD_MAPPING.stockist_product,
    VALIDATION_FIELD_MAPPING.op_qty,
    VALIDATION_FIELD_MAPPING.op_qty_value,
    VALIDATION_FIELD_MAPPING.received_qty,
    VALIDATION_FIELD_MAPPING.received_qty_value,
    VALIDATION_FIELD_MAPPING.issued_qty,
    VALIDATION_FIELD_MAPPING.issued_qty_value,
    VALIDATION_FIELD_MAPPING.closing_qty,
    VALIDATION_FIELD_MAPPING.closing_qty_value,
    VALIDATION_FIELD_MAPPING.company_product,
    VALIDATION_FIELD_MAPPING.mapping,
  ],
  'monthlyStockVerifyList'
);

const QTY_COLUMNS = new Set([
  VALIDATION_FIELD_MAPPING.op_qty,
  VALIDATION_FIELD_MAPPING.received_qty,
  VALIDATION_FIELD_MAPPING.issued_qty,
  VALIDATION_FIELD_MAPPING.closing_qty,
]);

const VALUE_COLUMNS = new Set([
  VALIDATION_FIELD_MAPPING.op_qty_value,
  VALIDATION_FIELD_MAPPING.received_qty_value,
  VALIDATION_FIELD_MAPPING.issued_qty_value,
  VALIDATION_FIELD_MAPPING.closing_qty_value,
]);

const DIVISION_ROW_HEIGHT = 48; // px per row
const DIVISION_MAX_ROWS = 5;
const DIVISION_MAX_HEIGHT = DIVISION_ROW_HEIGHT * DIVISION_MAX_ROWS;

// ─── Component ────────────────────────────────────────────────────────────────

const VerifyStock: React.FC = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams<{ mode: string; id: string }>();
  const { t } = useTranslation();

  const monthlyStockId = Number(id);
  const isViewMode = mode === 'view';

  const [summary, setSummary] = useState<any>({});
  const [kpis, setKpis] = useState({ total_products: 0, closing_value: 0, secondary_value: 0, mapped: 0, unmapped: 0 });
  const [divisions, setDivisions] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Status tracked locally — API only called on Save
  const [localStatus, setLocalStatus] = useState<{ code: string; name: string } | null>(null);

  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showUnmatchedOnly, setShowUnmatchedOnly] = useState(false);
  const [columnView, setColumnView] = useState<ColumnView>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [activeStockistProductId, setActiveStockistProductId] = useState<number | null>(null);
  const [activeStockistProductName, setActiveStockistProductName] = useState('');
  const [activeInitialDivisionId, setActiveInitialDivisionId] = useState('');
  const [activeInitialProductId, setActiveInitialProductId] = useState('');

  useFetch({
    service: MonthlyStockService,
    func: 'getSummary',
    payload: { id: monthlyStockId },
    dependency: [monthlyStockId],
    skip: !monthlyStockId,
    onSuccess: (response) => {
      setSummary(response ?? {});
      if (response?.status) {
        setLocalStatus(response.status);
      }
    },
  });

  const { loading } = useFetch({
    service: MonthlyStockService,
    func: 'getVerifyData',
    payload: { id: monthlyStockId },
    dependency: [monthlyStockId, refreshKey],
    skip: !monthlyStockId,
    onSuccess: (response) => {
      setKpis(response?.kpis ?? { total_products: 0, closing_value: 0, secondary_value: 0, mapped: 0, unmapped: 0 });
      setDivisions(response?.divisions ?? []);
    },
  });

  // ── Map product ────────────────────────────────────────────────────────────
  const handleMapProduct = useCallback((stockistProductId: number, stockistProductName: string, initialDivisionId = '', initialProductId = '') => {
    setActiveStockistProductId(stockistProductId);
    setActiveStockistProductName(stockistProductName);
    setActiveInitialDivisionId(initialDivisionId);
    setActiveInitialProductId(initialProductId);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setActiveStockistProductId(null);
    setActiveStockistProductName('');
    setActiveInitialDivisionId('');
    setActiveInitialProductId('');
  }, []);

  const handleMapped = useCallback(() => {
    setModalOpen(false);
    setActiveStockistProductId(null);
    setActiveStockistProductName('');
    setActiveInitialDivisionId('');
    setActiveInitialProductId('');
    setRefreshKey((prev) => prev + 1);
  }, []);

  // ── Status dropdown — local only, no API call ──────────────────────────────
  const handleStatusChange = useCallback((code: string, name: string) => {
    setLocalStatus({ code, name });
    setCommentError('');
  }, []);

  // ── Save — validate then call API ──────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!monthlyStockId || !localStatus) return;

    if (localStatus.code === 'APPROVED' && kpis.unmapped > 0) {
      setCommentError(t('monthlyStock.verifyStock.pageTexts.footer.errors.unmappedProducts'));
      return;
    }

    if (localStatus.code === 'REJECTED' && !comment.trim()) {
      setCommentError(t('monthlyStock.verifyStock.pageTexts.footer.errors.commentRequired'));
      return;
    }

    try {
      setSaveLoading(true);
      await MonthlyStockService.updateStatus(monthlyStockId, localStatus.code);
      navigate(Modules.monthlyStock + AuthRoutes.monthlyStockList);
    } catch {
      // handled by interceptor
    } finally {
      setSaveLoading(false);
    }
  }, [monthlyStockId, localStatus, kpis.unmapped, comment, navigate, t]);

  const handleCancel = useCallback(() => {
    navigate(Modules.monthlyStock + AuthRoutes.monthlyStockList);
  }, [navigate]);

  // ── Download presigned URL ─────────────────────────────────────────────────
  const handleDownload = useCallback(async () => {
    try {
      setDownloadLoading(true);
      const response: any = await MonthlyStockService.getDownloadUrl({ id: monthlyStockId });
      console.log("Resposne url ====", response)
      window.open(response?.url, '_blank');
    } catch {
      // handled by interceptor
    } finally {
      setDownloadLoading(false);
    }
  }, [monthlyStockId]);

  // ── Filtered head cells per column view ───────────────────────────────────
  const visibleHeadCells = useMemo(() => {
    let cells = ALL_HEAD_CELLS;
    if (columnView === 'qty') cells = ALL_HEAD_CELLS.filter((hc) => !VALUE_COLUMNS.has(hc.id));
    else if (columnView === 'values') cells = ALL_HEAD_CELLS.filter((hc) => !QTY_COLUMNS.has(hc.id));
    return cells.map((hc) => hc.id === 'status' ? { ...hc, textAlign: 'center' as const } : hc);
  }, [columnView]);

  // ── Filtered divisions for Show Unmatched Only ─────────────────────────────
  const visibleDivisions = useMemo(
    () => showUnmatchedOnly ? divisions.filter((d) => d.id === null) : divisions,
    [divisions, showUnmatchedOnly]
  );

  // ── Base TableContext value — data/total overridden per division ───────────
  const baseContextValue = useMemo(
    () => ({
      headCells: visibleHeadCells,
      rowProps: { onMapProduct: handleMapProduct, viewOnly: isViewMode },
      row: StockProductRow,
      order: SortOrder.asc as any,
      orderBy: '',
      page: 1,
      perPage: 9999,
      loading: false,
      pagination: false,
      stickyHeader: true,
      noDataFoundMessage: '',
      setPage: () => { },
      setPerPage: () => { },
      setOrder: () => { },
      setOrderBy: () => { },
    }),
    [visibleHeadCells, handleMapProduct, isViewMode]
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Header
        stockistName={summary?.stockist || '-'}
        period={summary?.period || '-'}
        status={localStatus ?? { code: '', name: '-' }}
        mode={isViewMode ? 'view' : 'edit'}
        onStatusChange={isViewMode ? undefined : handleStatusChange}
        onDownload={handleDownload}
        downloadLoading={downloadLoading}
      />

      <StatsBar
        totalProducts={kpis.total_products}
        closingValue={kpis.closing_value}
        secondaryValue={kpis.secondary_value}
        mapped={kpis.mapped}
        unmapped={kpis.unmapped}
      />

      <div className="card">
        {/* Table header */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0 d-flex align-items-center">
            <i className="ri-list-check-2-line me-2 text-primary"></i>
            {t('monthlyStock.verifyStock.pageTexts.tableHeader.stockData')}
          </h5>
          <div className="d-flex align-items-center gap-4">
            {/* Column view radio buttons */}
            <div className="d-flex align-items-center gap-3">
              {(['all', 'values', 'qty'] as ColumnView[]).map((view) => (
                <div key={view} className="form-check form-check-inline mb-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="columnView"
                    id={`col-${view}`}
                    checked={columnView === view}
                    onChange={() => setColumnView(view)}
                  />
                  <label className="form-check-label small" htmlFor={`col-${view}`}>
                    {t(`monthlyStock.verifyStock.pageTexts.columnView.${view}`)}
                  </label>
                </div>
              ))}
            </div>

            {/* Show Unmatched Only toggle */}
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="showUnmatchedOnly"
                checked={showUnmatchedOnly}
                onChange={(e) => setShowUnmatchedOnly(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="showUnmatchedOnly">
                {t('monthlyStock.verifyStock.pageTexts.statsBar.showUnmatchedOnly')}
              </label>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4">
              <span className="spinner-border spinner-border-sm me-2" />
            </div>
          ) : visibleDivisions.length === 0 ? (
            <div className="text-center py-4 text-muted">
              {t('monthlyStock.list.pageTexts.table.noDataFoundMessage')}
            </div>
          ) : (
            visibleDivisions.map((division) => (
              <div key={division.id ?? 'UNKNOWN'} className="border-bottom">
                {/* Division label */}
                <div className="bg-light px-3 py-2 fw-semibold text-uppercase small border-bottom" style={{ letterSpacing: '0.05em' }}>
                  {t('monthlyStock.verifyStock.pageTexts.row.divisionPrefix')}{division.name}
                </div>

                {/* Division table — scrollable, uses common Table component */}
                <TableContext.Provider value={{ ...baseContextValue, data: division.products, total: division.products.length }}>
                  <div className="table-responsive" style={{ maxHeight: DIVISION_MAX_HEIGHT, overflowY: 'auto' }}>
                    <Table />
                  </div>
                </TableContext.Provider>
              </div>
            ))
          )}
        </div>

        <Footer
          mode={isViewMode ? 'view' : 'edit'}
          comment={comment}
          onCommentChange={(v) => {
            setComment(v);
            if (v.trim()) setCommentError('');
          }}
          onSave={handleSave}
          onCancel={handleCancel}
          saveLoading={saveLoading}
          commentError={commentError}
        />
      </div>

      <StockMappingModal
        open={modalOpen}
        monthlyStockId={monthlyStockId}
        stockistProductId={activeStockistProductId}
        stockistProductName={activeStockistProductName}
        initialDivisionId={activeInitialDivisionId}
        initialProductId={activeInitialProductId}
        onClose={handleModalClose}
        onMapped={handleMapped}
      />
    </>
  );
};

export default VerifyStock;
